/**
 * Dashboard (e.g. automate.workload.co) deep-links here with:
 *   t = tenant_id → localStorage "tenant"
 *   u = user_id   → localStorage "user"
 *   c = JWT       → localStorage "tok_"
 * After persisting, only c is removed from the URL (token should not stay in the address bar).
 * u and t remain in the query string so links stay shareable/bookmarkable like legacy behavior.
 *
 * Expired / invalid sessions redirect to Automate sign-in with ?next=<current editor URL>.
 * Scaffold (automate) captures `next`, and after login appends c/u/t for editor.workload.co
 * so applyDashboardAuthFromUrl() can restore the session and return the user here.
 */
const KEYS = [
  { param: "t", storageKey: "tenant" },
  { param: "u", storageKey: "user" },
  { param: "c", storageKey: "tok_" },
];

const AUTOMATE_SIGNIN_BASE = "https://automate.workload.co/auth/signin";
const AUTH_ME_URL = "https://backend.workload.co/api/auth/me";

/** Routes that must stay reachable without a valid JWT. */
const PUBLIC_PATH_PREFIXES = [
  "/agents/share/",
  "/form/",
  "/page/",
  "/shopify",
];

export function applyDashboardAuthFromUrl() {
  if (typeof window === "undefined" || !window.location) return false;
  let search = window.location.search || "";
  if (!search || search === "?") return false;
  let params;
  try {
    params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  } catch {
    return false;
  }
  let wrote = false;
  for (const { param, storageKey } of KEYS) {
    if (!params.has(param)) continue;
    const raw = params.get(param);
    if (raw == null) continue;
    const v = String(raw).trim();
    if (v === "") continue;
    try {
      localStorage.setItem(storageKey, v);
      wrote = true;
    } catch (_) {
      /* quota / private mode */
    }
  }
  const hadJwtInUrl = params.has("c");
  if (hadJwtInUrl) {
    params.delete("c");
  }
  const qs = params.toString();
  const next =
    window.location.pathname +
    (qs ? `?${qs}` : "") +
    (window.location.hash || "");
  const current =
    window.location.pathname +
    (window.location.search || "") +
    (window.location.hash || "");
  if (next === current) {
    return wrote;
  }
  try {
    window.history.replaceState(window.history.state || {}, "", next);
  } catch {
    window.history.replaceState({}, "", next);
  }
  return wrote || hadJwtInUrl;
}

export function getDashboardTenant() {
  try {
    return localStorage.getItem("tenant") || "";
  } catch {
    return "";
  }
}

export function getDashboardUser() {
  try {
    return localStorage.getItem("user") || "";
  } catch {
    return "";
  }
}

export function getDashboardToken() {
  try {
    return localStorage.getItem("tok_") || "";
  } catch {
    return "";
  }
}

export function isPublicEditorPath(pathname) {
  const path =
    pathname ||
    (typeof window !== "undefined" && window.location
      ? window.location.pathname
      : "");
  if (!path) return false;
  return PUBLIC_PATH_PREFIXES.some(
    (prefix) => path === prefix.replace(/\/$/, "") || path.startsWith(prefix),
  );
}

/** Editor root keeps its own missing-token → signup UX (templates, etc.). */
function shouldDeferMissingTokenToPage(pathname) {
  const path =
    pathname ||
    (typeof window !== "undefined" && window.location
      ? window.location.pathname
      : "");
  return path === "/" || path === "";
}

export function getAutomateSignInUrl(nextUrl) {
  const next =
    nextUrl ||
    (typeof window !== "undefined" && window.location
      ? window.location.href
      : "");
  return `${AUTOMATE_SIGNIN_BASE}?next=${encodeURIComponent(next)}`;
}

export function redirectToAutomateSignIn(nextUrl) {
  if (typeof window === "undefined") return;
  window.location.replace(getAutomateSignInUrl(nextUrl));
}

/**
 * Validate the stored JWT via /api/auth/me. On non-200 (expired/invalid),
 * clear tok_ and send the user to Automate sign-in with next= current URL.
 * @returns {Promise<{ ok: boolean, user?: object|null }>}
 */
export async function ensureValidAuthSession() {
  if (typeof window === "undefined" || !window.location) {
    return { ok: true, user: null };
  }
  if (isPublicEditorPath()) {
    return { ok: true, user: null };
  }

  const token = getDashboardToken();
  if (!token) {
    if (shouldDeferMissingTokenToPage()) {
      return { ok: true, user: null };
    }
    redirectToAutomateSignIn();
    return { ok: false, user: null };
  }

  try {
    const response = await fetch(AUTH_ME_URL, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status !== 200) {
      try {
        localStorage.removeItem("tok_");
      } catch (_) {
        /* ignore */
      }
      redirectToAutomateSignIn();
      return { ok: false, user: null };
    }
    let user = null;
    try {
      user = await response.json();
    } catch (_) {
      user = null;
    }
    return { ok: true, user };
  } catch (_) {
    // Network blip — do not bounce an otherwise valid session.
    return { ok: true, user: null };
  }
}
